import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { Question } from '@aphp-ema/common/questionnaire';
import { getPreviewText } from '@/features/questionnaires/utils/conditionPreview';
import { getQuestionTypeIcon } from '@/features/questionnaires/utils/questionTypeIcons';
import type { BrokenCondition } from '@/features/questionnaires/utils/checkQuestionChangeConditions';
import { useInitialQuestions } from '@/features/questionnaires/hooks/useInitialQuestions';
type WarningType = 'reorder' | 'typeChange' | 'optionChange' | 'deletion';

type ConditionWarningDialogProps = {
  open: boolean;
  onConfirm:
    | ((action: 'cancel' | 'proceed' | 'proceedAndClear') => void)
    | null;
  brokenConditions: BrokenCondition[];
  questions: Question[];
  warningType: WarningType;
};

export const ConditionWarningDialog: React.FC<ConditionWarningDialogProps> = ({
  open,
  onConfirm,
  brokenConditions,
  questions,
  warningType,
}) => {
  const { t } = useTranslation();

  const initialQuestions = useInitialQuestions();

  const getQuestionLabel = (questionId: string): string => {
    const question = questions.find((q) => q.id === questionId);
    const index = questions.findIndex((q) => q.id === questionId);
    return (
      question?.label ||
      `${t('pages.studies.questions.title', { index: index + 1 })}`
    );
  };

  return (
    <Dialog open={open} onClose={() => onConfirm?.('cancel')} maxWidth="sm">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6">
            {t(`pages.studies.questions.conditionWarning.${warningType}.title`)}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t(
            `pages.studies.questions.conditionWarning.${warningType}.message`,
            {
              count: brokenConditions.length,
            },
          )}
        </Alert>

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {/* Header row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 2fr',
              borderBottom: '1px solid',
              borderBottomColor: 'divider',
              backgroundColor: 'grey.50',
            }}
          >
            <Box
              sx={{
                py: 1,
                px: 1.5,
                borderRight: '1px solid',
                borderRightColor: 'divider',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('pages.studies.questions.title', { index: '' }).replace(
                  ' ',
                  '',
                )}
              </Typography>
            </Box>
            <Box sx={{ py: 1, px: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('pages.studies.questions.conditions.condition')}
              </Typography>
            </Box>
          </Box>

          {brokenConditions.map((brokenCondition, index) => {
            const question = questions.find(
              (q) => q.id === brokenCondition.questionId,
            );
            const questionType = question?.parameters?.type || 'text';
            const conditionText = getPreviewText(
              brokenCondition.condition,
              questions,
              initialQuestions,
              t,
            );

            return (
              <Box
                key={`${brokenCondition.questionId}-${index}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  borderBottom:
                    index < brokenConditions.length - 1 ? '1px solid' : 'none',
                  borderBottomColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    py: 1,
                    px: 1.5,
                    borderRight: '1px solid',
                    borderRightColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {getQuestionTypeIcon(questionType)}
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {getQuestionLabel(brokenCondition.questionId)}
                  </Typography>
                </Box>
                <Box
                  sx={{ py: 1, px: 1.5, display: 'flex', alignItems: 'center' }}
                >
                  <Typography variant="body2">{conditionText}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => onConfirm?.('cancel')} color="primary">
          {t('buttons.cancel')}
        </Button>
        <Button
          onClick={() => onConfirm?.('proceedAndClear')}
          color="warning"
          variant="outlined"
        >
          {t('pages.studies.questions.conditionWarning.proceedAndClear')}
        </Button>
        <Button
          onClick={() => onConfirm?.('proceed')}
          variant="contained"
          color="primary"
        >
          {t('pages.studies.questions.conditionWarning.proceedAndEdit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
